// CreateProductForm.tsx
import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import Header from "@commons/header";
import Column from "@components/column";
import Row from "@components/row";
import Fields from "@components/fields";
import Button from "@components/button";
import FieldWrapper from "@components/wrapper/field-wrapper";
import ButtonWrapper from "@components/wrapper/button-wrapper";

import { getInitialValues, transformProductData } from "./initialValue";
import { FormValues } from "../types";
import useIsMobile from "@hooks/useIsMobile";
import { useProductForm } from "@hooks/useProductForm";
import { useProductSubmit } from "@hooks/useProductSubmit";
import { Product } from "@apis/index";
import { ProductBasicInfo } from "./ProductBasicInfo";
import { PriceInfo } from "./PriceInfo";
import { StockInfo } from "./StockInfo";
import { AddonsSection } from "./AddonsSection";
import { RecommendationSection } from "./RecommendationSection";
import { VariantsSection } from "./VariantsSection";
import { SeoSection } from "./SeoSection";

import CommonForm from "@pages/admin/product-management/add-ons/CommonForm";
import { LensTab } from "./Lensaddons";
import { useDispatch, useSelector } from "react-redux";
import { Actions, RootState } from "@store/index";
import DeliveryInfo from "./DeliveryInfo";
import { useLocation } from "react-router-dom";

const CreateProductForm = ({ data }: any) => {
  console.log({ data });
  const isMobile = useIsMobile(800);
  const location = useLocation();
  const {
    productData,
    user,
    selectedColors,
    selectedAttributes,
    variantError,
    setVariantError,
    setSelectedColors,
    setSelectedAttributes,
    fetchSingleAttribute,
    fetchSingleColor,
    fetchSubCategories,
    attributes,
    colors,
    categories,
    subcategories,
    brands,
    units,
    brandOptions,
    categoryOptions,
    subCategoryOptions,
    moduleId,
    storeId,
    isPureVeg,
    fetchProduct,
  } = useProductForm(data);
  const { handleSubmit } = useProductSubmit(productData, user, storeId);
  const [initialValues, setInitialValues] =
    useState<FormValues>(getInitialValues());

  useEffect(() => {
    if (location.state?.taskData) {
      setInitialValues({
        ...getInitialValues(),
        ...location.state.taskData,
      });
    } else if (productData) {
      const transformedData = transformProductData(productData);
      setInitialValues(transformedData);
      if (productData.category?.id || productData.categoryId) {
        fetchSubCategories(productData.category?.id || productData.categoryId);
      }
    }
  }, [productData, location.state]);

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue }) => (
        <Form>
          <Column>
            <Header title={!!productData ? "Update Product" : "Add Product"} />

            {/* Basic Product Info */}
            <Row>
              <FieldWrapper styleWrapper="width:100%">
                <div style={{ flex: 1, width: "100%" }}>
                  <Column stylecolumn="row-gap:34px">
                    <Fields.Input
                      name="name"
                      label="Product"
                      placeholder="Enter Product Name"
                    />
                    <Fields.Editor
                      name="description"
                      label="Product Description"
                    />
                  </Column>
                </div>
              </FieldWrapper>

              <FieldWrapper styleWrapper="width:100%">
                <Row styleRow="align-items:stretch">
                  <Fields.Upload
                    name="image"
                    multiple
                    size={60}
                    label="Product Images"
                    placeholder="Upload at least one image (max:5)"
                    maxFiles={5}
                    mode={!!productData?.id ? "update" : "create"}
                    onRemoveImage={async (val) => {
                      await Product.removeImage({
                        state: "products",
                        id: val.id,
                      });
                    }}
                  />
                  <Fields.Upload
                    name="video"
                    label="Product Video"
                    video
                    placeholder="Upload at most one video or leave empty"
                    size={60}
                    useRawFile={true}
                  />
                </Row>
              </FieldWrapper>
            </Row>

            {/* Modular Sections */}
            <ProductBasicInfo
              values={values}
              setFieldValue={setFieldValue}
              categoryOptions={categoryOptions}
              subCategoryOptions={subCategoryOptions}
              brandOptions={brandOptions}
              units={units}
              fetchSubCategories={fetchSubCategories}
              isPureVeg={isPureVeg}
            />
            <PriceInfo
              values={values}
              disabled={user?.role === "store" && !!productData}
            />
            <StockInfo values={values} />
            <DeliveryInfo values={values} setFieldValue={setFieldValue} />
            <AddonsSection values={values} />
            <RecommendationSection product={productData} />
            {/* <LensTab /> */}
            <VariantsSection
              values={values}
              setFieldValue={setFieldValue}
              selectedColors={selectedColors}
              selectedAttributes={selectedAttributes}
              variantError={variantError}
              setVariantError={setVariantError}
              setSelectedAttributes={setSelectedAttributes}
              fetchSingleAttribute={fetchSingleAttribute}
              fetchSingleColor={fetchSingleColor}
              attributes={attributes}
              colors={colors}
              productData={productData}
              isMobile={isMobile}
              setSelectedColors={setSelectedColors}
            />

            <SeoSection values={values} />

            <FieldWrapper styleWrapper="width:100%; margin-top: 20px;">
              <Row styleRow="align-items: center; gap: 10px;">
                <Fields.Checkbox
                  name="useQueue"
                  label="Background Upload (Recommended for large videos/images)"
                />
              </Row>
            </FieldWrapper>

            <ButtonWrapper>
              <Button title="Reset" type="reset" variant="reset" />
              <Button
                title={!!productData ? "Update" : "Submit"}
                type="submit"
              />
            </ButtonWrapper>
          </Column>
        </Form>
      )}
    </Formik>
  );
};

export default CreateProductForm;
